import { useMemo } from "react"
import { OK, ERROR } from "helpers/data-statuses"
import { Filter } from "connected-components/dashboard/context/types"
import { IsoDate } from "helpers/types/dates"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useClassificationsData } from "resources/classifications"
import { computeSegmentHeaderLabel } from "../position-sets"

export default function useSegmentHeaderLabel(
  instrument: SelectedTypedInstrument,
  currentDate: IsoDate,
  filters: Filter | null,
  normalization: string | null,
  currency: string,
): string | null {
  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const { classifications, classificationsValues, status: classificationsStatus } = useClassificationsData(currentDate, undefined)

  return useMemo(() => computeSegmentHeaderLabel(filters, normalization, currency, {
    tagCategories,
    classificationsMetas: classifications,
    classificationsValues,
    attributeCategories,
    metadataReady: {
      tag: tagCategoriesStatus === OK,
      classification: classificationsStatus === OK,
      positionAttributes: attributeCategoriesStatus === OK,
    },
  }), [filters, normalization, currency, tagCategories, tagCategoriesStatus, classifications, classificationsValues, classificationsStatus, attributeCategories, attributeCategoriesStatus])
}

export interface SegmentLabelInput {
  filters: Filter | null
  normalization: string | null
}

// Tag and attribute categories are per portfolio, so one call can only label filters that no longer carry them.
export function useStrippedSegmentHeaderLabels(
  segments: SegmentLabelInput[],
  currentDate: IsoDate,
  currency: string,
): { labels: (string | null)[], isReady: boolean } {
  const { classifications, classificationsValues, status: classificationsStatus } = useClassificationsData(currentDate, undefined)

  const isReady = classificationsStatus === OK || classificationsStatus === ERROR || !segments.some(({ filters }) => filters?.classification)

  const labels = useMemo(() => segments.map(({ filters, normalization }) => computeSegmentHeaderLabel(filters, normalization, currency, {
    classificationsMetas: classifications,
    classificationsValues,
    metadataReady: { tag: false, classification: classificationsStatus === OK, positionAttributes: false },
  })), [segments, currency, classifications, classificationsValues, classificationsStatus])

  return { labels, isReady }
}
